/**
 * Normalize / sort statute section arrays for the Library UI.
 *
 * Firestore JSON may use numeric `id`s (compare unequal to string state), omit
 * `order`, or duplicate ids — all of which break the section list and selection.
 */

export function librarySectionSortKey(s: { label?: string }): number {
  const label = String(s.label || '').trim();
  const low = label.toLowerCase();
  if (low === 'preamble' || low.includes('preamble')) return -1_000_000;
  if (low === 'introduction' || low.includes('introduction')) return -999_000;
  if (/^\d+$/.test(label)) return parseInt(label, 10);
  const m = label.match(/(\d+)/);
  if (m) return parseInt(m[1], 10);
  return 500_000;
}

function ensureUniqueSectionIds(sections: Record<string, unknown>[]): Record<string, unknown>[] {
  const seen = new Set<string>();
  return sections.map((s, idx) => {
    const rawId = s.id;
    let id =
      rawId !== undefined && rawId !== null && String(rawId).trim() !== ''
        ? String(rawId)
        : `section-${idx}`;
    if (seen.has(id)) {
      let n = 2;
      while (seen.has(`${id}__${n}`)) n += 1;
      id = `${id}__${n}`;
    }
    seen.add(id);
    return { ...s, id };
  });
}

export function normalizeLibrarySections(raw: unknown[]): Record<string, unknown>[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const objects = raw.filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object');
  const withOrder = objects.map((s, idx) => ({
    ...s,
    order:
      typeof s.order === 'number' && !Number.isNaN(s.order as number)
        ? (s.order as number)
        : idx,
  }));
  const unique = ensureUniqueSectionIds(withOrder);
  return [...unique].sort((a, b) => {
    const ka = librarySectionSortKey(a as { label?: string; order?: number });
    const kb = librarySectionSortKey(b as { label?: string; order?: number });
    if (ka !== kb) return ka - kb;
    return (a.order as number) - (b.order as number);
  });
}

/** Reading-order preserve (by `order` only) for statute preview — avoids mixing Section 10 before Section 2. */
export function normalizePreviewSections(raw: unknown[]): Record<string, unknown>[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const objects = raw.filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object');
  const withOrder = objects.map((s, idx) => ({
    ...s,
    order:
      typeof s.order === 'number' && !Number.isNaN(s.order as number)
        ? (s.order as number)
        : idx,
  }));
  const unique = ensureUniqueSectionIds(withOrder);
  return [...unique].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
}

/** Sidebar bucket key when `tocPartLabel` is missing (flat / legacy payloads). */
export const PREVIEW_NAV_UNGROUPED_KEY = '__ungrouped__';

export type PreviewNavGroup<T> = {
  key: string;
  /** Shown under the main “Sections” heading; use `PREVIEW_NAV_UNGROUPED_KEY` for legacy rows. */
  heading: string;
  sections: T[];
};

/**
 * Split preview sections into consecutive groups by `tocPartLabel` (Preamble, Preliminary, Part I, Order …).
 * Preserves document order.
 */
export function groupPreviewNavSections<T extends { tocPartLabel?: string }>(sections: T[]): PreviewNavGroup<T>[] {
  const groups: PreviewNavGroup<T>[] = [];
  for (const s of sections) {
    const trimmed = s.tocPartLabel && String(s.tocPartLabel).trim();
    const key = trimmed || PREVIEW_NAV_UNGROUPED_KEY;
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.sections.push(s);
    } else {
      groups.push({
        key,
        heading: trimmed || 'Other',
        sections: [s],
      });
    }
  }
  return groups;
}

/** Match URL hash to a section id after normalization (handles section-9 vs 9). */
export function resolveLibrarySectionIdFromHash(
  hash: string,
  sections: { id?: unknown; label?: unknown }[],
): string | null {
  if (!hash || !sections.length) return null;
  const h = decodeURIComponent(hash).trim();
  const ids = sections.map((s) => String(s.id ?? ''));
  if (ids.includes(h)) return h;
  const stripped = h.replace(/^section-/i, '');
  if (ids.includes(stripped)) return stripped;
  const lowered = h.toLowerCase();
  const byLabel = sections.find(
    (s) =>
      String(s.label || '')
        .toLowerCase()
        .replace(/\s+/g, '-') === lowered,
  );
  if (byLabel && byLabel.id !== undefined && byLabel.id !== null) {
    return String(byLabel.id);
  }
  return null;
}
